/**
 * @fileoverview FlowGrid Consent Manager
 * @module consent/manager
 *
 * @description
 * Framework-agnostic consent manager that stores user cookie preferences,
 * integrates with the FlowGrid transport layer's consent gates, and
 * respects DNT / GPC signals.
 *
 * The consent manager uses a single cookie (`fg_consent` by default) to
 * persist the user's per-category choices. It exposes a simple API that
 * framework-specific cookie banner components can wrap.
 *
 * @example
 * ```ts
 * import { ConsentManager } from 'flowgrid-sdk';
 * import { FlowGridTransport } from 'flowgrid-sdk';
 *
 * const consent = new ConsentManager({
 *   onChange: (prefs) => {
 *     // Sync with FlowGrid transport consent gates
 *     FlowGridTransport.setConsent({
 *       analytics: prefs.analytics,
 *       marketing: prefs.marketing,
 *     });
 *   },
 * });
 *
 * // Check if user has already consented
 * if (!consent.hasConsented()) {
 *   showCookieBanner();
 * }
 *
 * // Accept all
 * consent.acceptAll();
 *
 * // Or reject non-essential
 * consent.rejectNonEssential();
 *
 * // Or update individual categories
 * consent.update({ analytics: true, marketing: false, preferences: true });
 *
 * // Read current state
 * const prefs = consent.getPreferences();
 * console.log(prefs.analytics); // true
 * ```
 */

import type {
  ConsentConfig,
  ConsentPreferences,
} from './types';
import { registerConsentManager } from './registry';

// ============================================================================
// DEFAULTS
// ============================================================================

const DEFAULT_COOKIE_NAME = 'fg_consent';
const DEFAULT_TRACKING_COOKIE_NAME = 'fg_tracking_consent';
const DEFAULT_COOKIE_DAYS = 365;

const DEFAULT_PREFERENCES: ConsentPreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
  preferences: true,
};

// ============================================================================
// CONSENT MANAGER
// ============================================================================

/**
 * Manages cookie consent state for GDPR/CCPA compliance.
 *
 * Stores the user's per-category consent choices in a single cookie and
 * provides methods to accept, reject, or customise preferences. Integrates
 * with the FlowGrid transport layer via the `onChange` callback.
 *
 * @example
 * ```ts
 * import { ConsentManager, FlowGridTransport } from 'flowgrid-sdk';
 *
 * const consent = new ConsentManager({
 *   onChange: (prefs) => {
 *     FlowGridTransport.setConsent({
 *       analytics: prefs.analytics,
 *       marketing: prefs.marketing,
 *     });
 *   },
 * });
 *
 * // In your cookie banner "Accept All" handler:
 * consent.acceptAll();
 *
 * // In your "Reject All" handler:
 * consent.rejectNonEssential();
 *
 * // In your "Save Preferences" handler:
 * consent.update({ analytics: true, marketing: false, preferences: true });
 * ```
 */
export class ConsentManager {
  private config: Required<Omit<ConsentConfig, 'onChange' | 'defaultPreferences' | 'cookieDomain'>> & {
    onChange?: (preferences: ConsentPreferences) => void;
    defaultPreferences: ConsentPreferences;
    cookieDomain?: string;
  };
  private preferences: ConsentPreferences;

  /**
   * Create a new ConsentManager.
   *
   * @param config - Consent configuration options.
   *
   * @example
   * ```ts
   * const consent = new ConsentManager({
   *   cookieName: 'my_app_consent',
   *   cookieDays: 180,
   *   respectDNT: true,
   *   requireExplicitConsent: true,
   *   onChange: (prefs) => {
   *     console.log('Consent changed:', prefs);
   *   },
   * });
   * ```
   */
  constructor(config?: ConsentConfig) {
    this.config = {
      cookieName: config?.cookieName ?? DEFAULT_COOKIE_NAME,
      cookieDays: config?.cookieDays ?? DEFAULT_COOKIE_DAYS,
      cookieDomain: config?.cookieDomain,
      respectDNT: config?.respectDNT ?? true,
      // Permissive by default: FlowGrid collects data unless the customer
      // explicitly opts into a consent gate (`requireExplicitConsent: true` or
      // the banner's `mode: 'opt-in'`). Implementing consent is the customer's
      // responsibility — the SDK does not block essential or analytics data,
      // including session replay, out of the box.
      requireExplicitConsent: config?.requireExplicitConsent ?? false,
      // Logging in = accepting tracking (see registry.ts). Opt out per manager.
      grantOnAuth: config?.grantOnAuth ?? true,
      onChange: config?.onChange,
      defaultPreferences: {
        ...DEFAULT_PREFERENCES,
        ...config?.defaultPreferences,
        necessary: true, // always true
      },
    };

    // Load saved preferences or use defaults
    const saved = this.loadFromCookie();
    if (saved) {
      this.preferences = saved;
    } else if (this.isDNTEnabled() && this.config.respectDNT) {
      // DNT/GPC: default to rejecting non-essential
      this.preferences = {
        necessary: true,
        analytics: false,
        marketing: false,
        preferences: false,
      };
    } else if (this.config.requireExplicitConsent) {
      this.preferences = { ...this.config.defaultPreferences };
    } else {
      // Implied consent mode: all enabled by default
      this.preferences = {
        necessary: true,
        analytics: true,
        marketing: true,
        preferences: true,
      };
    }

    // Logging in = accepting tracking: the auth helpers auto-acceptAll() on
    // every registered manager. Opt out per manager with `grantOnAuth: false`.
    if (config?.grantOnAuth !== false) {
      registerConsentManager(this);
    }
  }

  // ---------------------------------------------------------------------------
  // PUBLIC API
  // ---------------------------------------------------------------------------

  /**
   * Check if the user has previously given consent (cookie exists).
   *
   * @returns `true` if the consent cookie exists, `false` otherwise.
   *
   * @example
   * ```ts
   * if (!consent.hasConsented()) {
   *   showCookieBanner();
   * }
   * ```
   */
  hasConsented(): boolean {
    return this.loadFromCookie() !== null;
  }

  /**
   * Get the current consent preferences.
   *
   * @returns A copy of the current {@link ConsentPreferences}.
   *
   * @example
   * ```ts
   * const prefs = consent.getPreferences();
   * if (prefs.analytics) {
   *   initAnalytics();
   * }
   * ```
   */
  getPreferences(): ConsentPreferences {
    return { ...this.preferences };
  }

  /**
   * Check if a specific consent category is enabled.
   *
   * @param category - The category to check.
   * @returns `true` if the category is consented to.
   *
   * @example
   * ```ts
   * if (consent.hasCategory('marketing')) {
   *   loadMarketingPixels();
   * }
   * ```
   */
  hasCategory(category: keyof ConsentPreferences): boolean {
    return this.preferences[category] ?? false;
  }

  /**
   * Accept all consent categories.
   *
   * @example
   * ```ts
   * // "Accept All" button handler
   * document.getElementById('accept-all')?.addEventListener('click', () => {
   *   consent.acceptAll();
   * });
   * ```
   */
  acceptAll(): void {
    this.setPreferences({
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
    });
  }

  /**
   * Reject all non-essential categories.
   * Only `necessary` remains enabled.
   *
   * @example
   * ```ts
   * // "Reject All" button handler
   * consent.rejectNonEssential();
   * ```
   */
  rejectNonEssential(): void {
    this.setPreferences({
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false,
    });
  }

  /**
   * Update individual consent categories.
   *
   * @param updates - Partial preferences to merge.
   *
   * @example
   * ```ts
   * // "Save Preferences" handler
   * consent.update({
   *   analytics: true,
   *   marketing: false,
   *   preferences: true,
   * });
   * ```
   */
  update(updates: Partial<Omit<ConsentPreferences, 'necessary'>>): void {
    this.setPreferences({
      ...this.preferences,
      ...updates,
      necessary: true,
    });
  }

  /**
   * Reset consent — removes the cookie and restores defaults.
   * The cookie banner should be shown again after this.
   *
   * @example
   * ```ts
   * // "Reset Cookies" link
   * consent.reset();
   * ```
   */
  reset(): void {
    this.deleteCookie();
    this.preferences = { ...this.config.defaultPreferences };
  }

  // ---------------------------------------------------------------------------
  // PRIVATE
  // ---------------------------------------------------------------------------

  private setPreferences(prefs: ConsentPreferences): void {
    const previous = { ...this.preferences };
    this.preferences = { ...prefs, necessary: true };
    this.saveToCookie();

    if (this.config.onChange) {
      this.config.onChange(this.preferences);
    }
  }

  private isDNTEnabled(): boolean {
    if (typeof navigator === 'undefined') return false;
    if ((navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl) return true;
    if (navigator.doNotTrack === '1') return true;
    return false;
  }

  // ---------------------------------------------------------------------------
  // COOKIE I/O
  // ---------------------------------------------------------------------------

  private loadFromCookie(): ConsentPreferences | null {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(new RegExp('(^| )' + this.config.cookieName + '=([^;]+)'));
    if (!match) return null;
    try {
      const parsed = JSON.parse(decodeURIComponent(match[2]));
      if (typeof parsed === 'object' && parsed !== null && typeof parsed.necessary === 'boolean') {
        return { ...DEFAULT_PREFERENCES, ...parsed, necessary: true };
      }
      return null;
    } catch {
      return null;
    }
  }

  private saveToCookie(): void {
    if (typeof document === 'undefined') return;
    const value = encodeURIComponent(JSON.stringify(this.preferences));
    const maxAge = this.config.cookieDays * 24 * 60 * 60;
    const domain = this.config.cookieDomain ? `; Domain=${this.config.cookieDomain}` : '';
    const secure = typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${this.config.cookieName}=${value}; Path=/; Max-Age=${maxAge}; SameSite=Lax${secure}${domain}`;

    // Simple boolean cookie for track.js compatibility
    const trackingAllowed = this.preferences.analytics ? 'true' : 'false';
    document.cookie = `${DEFAULT_TRACKING_COOKIE_NAME}=${trackingAllowed}; Path=/; Max-Age=${maxAge}; SameSite=Lax${secure}${domain}`;
  }

  private deleteCookie(): void {
    if (typeof document === 'undefined') return;
    const domain = this.config.cookieDomain ? `; Domain=${this.config.cookieDomain}` : '';
    document.cookie = `${this.config.cookieName}=; Path=/; Max-Age=0${domain}`;
    document.cookie = `${DEFAULT_TRACKING_COOKIE_NAME}=; Path=/; Max-Age=0${domain}`;
  }
}
